import {
  UserSettingsFragment,
  UserSettingsFragmentDoc,
  useUpdateUserSettingsMutation,
} from './operation.generated';
import parseApolloError from '../../util/apolloError';
import { useUserContext } from '../../context/user/UserContext';

const useUpdateUserSettings = (props: { onError: (error: string) => void }) => {
  const { data: userQuery } = useUserContext();
  const { onError } = props;

  const [updateUserSettings] = useUpdateUserSettingsMutation({
    onCompleted: data => {
      if (data?.updateUserSettings?.success === false) {
        onError(data?.updateUserSettings?.message ?? null);
      }
    },
    onError: apolloError => {
      onError(parseApolloError(apolloError));
    },
    optimisticResponse: ({
      input: {
        defaultTTSVersion,
        displayTTSVersionSelector,
        globalAudioFileFormatSelected,
        globalAudioFileFormat,
        includeClipScriptFormats,
        doNotShowClipDeletionConfirmationModal,
        doNotShowClipPreviewDeletionConfirmationModal,
        doNotShowNavigationConfirmationModal,
        doNotShowDeleteTeamReplacementConfirmationModal,
        globalAudioPlaybackSetting,
      },
    }) => {
      return {
        updateUserSettings: {
          __typename: 'UpdateUserSettingsResponse',
          settings: {
            defaultTTSVersion:
              defaultTTSVersion ?? userQuery?.user.settings.defaultTTSVersion,
            displayTTSVersionSelector:
              displayTTSVersionSelector ??
              userQuery?.user.settings.displayTTSVersionSelector,
            globalAudioFileFormatSelected:
              globalAudioFileFormatSelected ??
              userQuery?.user.settings.globalAudioFileFormatSelected,
            globalAudioFileFormat:
              globalAudioFileFormat ??
              userQuery?.user.settings.globalAudioFileFormat,
            includeClipScriptFormats:
              includeClipScriptFormats ??
              userQuery?.user.settings.includeClipScriptFormats,
            doNotShowClipDeletionConfirmationModal:
              doNotShowClipDeletionConfirmationModal ??
              userQuery?.user.settings.doNotShowClipDeletionConfirmationModal,
            doNotShowClipPreviewDeletionConfirmationModal:
              doNotShowClipPreviewDeletionConfirmationModal ??
              userQuery?.user.settings
                .doNotShowClipPreviewDeletionConfirmationModal,
            doNotShowNavigationConfirmationModal:
              doNotShowNavigationConfirmationModal ??
              userQuery?.user.settings.doNotShowNavigationConfirmationModal,
            doNotShowDeleteTeamReplacementConfirmationModal:
              doNotShowDeleteTeamReplacementConfirmationModal ??
              userQuery?.user.settings
                .doNotShowDeleteTeamReplacementConfirmationModal,
            globalAudioPlaybackSetting:
              globalAudioPlaybackSetting ??
              userQuery?.user.settings.globalAudioPlaybackSetting,
            __typename: 'UserSettings',
          },
          success: true,
          code: 'graphql_mutation_success',
          message: 'Updated setting successfully',
        },
      };
    },
    update: (cache, { data, errors }) => {
      if (!errors && data?.updateUserSettings.success) {
        const cachedSettings = cache.readFragment<UserSettingsFragment>({
          id: `User:${userQuery?.user.id}`,
          fragment: UserSettingsFragmentDoc,
        });

        if (cachedSettings) {
          const mergedSettings = {
            ...cachedSettings?.settings,
            defaultTTSVersion:
              data?.updateUserSettings.settings?.defaultTTSVersion,
            displayTTSVersionSelector:
              data?.updateUserSettings.settings?.displayTTSVersionSelector,
            globalAudioFileFormatSelected:
              data?.updateUserSettings.settings?.globalAudioFileFormatSelected,
            globalAudioFileFormat:
              data?.updateUserSettings.settings?.globalAudioFileFormat,
            includeClipScriptFormats:
              data?.updateUserSettings.settings?.includeClipScriptFormats,
            doNotShowClipDeletionConfirmationModal:
              data?.updateUserSettings.settings
                ?.doNotShowClipDeletionConfirmationModal,
            doNotShowClipPreviewDeletionConfirmationModal:
              data?.updateUserSettings.settings
                ?.doNotShowClipPreviewDeletionConfirmationModal,
            doNotShowNavigationConfirmationModal:
              data?.updateUserSettings.settings
                ?.doNotShowNavigationConfirmationModal,
            doNotShowDeleteTeamReplacementConfirmationModal:
              data?.updateUserSettings.settings
                ?.doNotShowDeleteTeamReplacementConfirmationModal,
            globalAudioPlaybackSetting:
              data?.updateUserSettings.settings?.globalAudioPlaybackSetting,
          };

          cache.writeFragment<UserSettingsFragment>({
            id: `User:${userQuery?.user.id}`,
            fragment: UserSettingsFragmentDoc,
            data: { settings: mergedSettings },
          });
        }
      }
    },
  });

  return { updateUserSettings };
};

export default useUpdateUserSettings;
